/**
 * This is ProductPresale component consisting of perkLabel, perkMessage, perkAction, errorLabel, bookmarked, bookmarkAction and removeBookmarkAction. This will be used on the PDP page.
 *
 * @module views/components/ProductPresale
 * @memberof -Common
 */
import './ProductPresale.scss';

import React from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { CLIENT_ACTION_TYPES, DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';

import Tag from '@ulta/modules/Tag/Tag';

import * as utils from './ProductPresale';

/**
 * Represents a ProductPresale component
 *
 * @method
 * @param {ProductPresaleProps} props - React properties passed from composition
 * @returns ProductPresale
 */
export const ProductPresale = function( props ){
  const { bookmarked = defaultProps.bookmarked, isMyListOverlayOpen, wishListAction, fulfillmentType } = props;
  const { user } = useUserContext();
  const clickHandlerData = {
    url: props.perkAction?.url,
    loginStatus: user?.loginStatus,
    clientActionType: props.perkAction?.clientActionType,
    navigationType: props.perkAction?.navigationType
  };

  return (
    <>
      <div className='ProductPresale'>
        <div className='ProductPresale__leftColumn'>
          <div className='ProductPresale__Label'>
            <Tag
              style='default'
              message={ props.perkLabel }
            />

            <Button
              likeLink
              label={ props.perkAction?.label }
              className='ProductPresale__Url'
              onClick={ () => utils.clickHandler( clickHandlerData ) }
              action={ props.perkAction }
              crossButtonVisibility={ true }
            />
          </div>

          <div className='ProductPresale__leftColumn__info'>
            <Text textStyle='body-3'>{ props.perkMessage }</Text>
          </div>
        </div>

        <div className='ProductPresale__rightColumn'>
          <AsyncComponent
            id={ props.id }
            invokeMutation={ props.invokeMutation }
            bookmarkAction={ wishListAction || props.bookmarkAction }
            bookmarked={ bookmarked }
            commerceId={ props.commerceId }
            key='ProductBookmark'
            moduleName='ProductBookmark'
            productActionsProps={ props }
            productId={ props.productId }
            quantity={ props.quantity }
            removeBookmarkAction={ props.removeBookmarkAction }
            skuId={ props.skuId }
            state={ props.state }
            isMyListOverlayOpen={ isMyListOverlayOpen }
            fulfillmentType={ fulfillmentType }
          />
        </div>
      </div>
      { props.errorLabel && (
        <div className='ProductPresale__errorMessage'>
          <Text textStyle='body-3'>{ props.errorLabel }</Text>
        </div>
      ) }
    </>
  );
};

/**
 * A click handler function
 * @method clickHandler
 * @param {object} data - Data arguments
 * @param {boolean} data.loginStatus - User login status
 * @param {string} data.url - URL to redirect to
 * @param {string} data.clientActionType - User clientActionType
 * @param {string} data.navigationType - User navigationType
 */
export const clickHandler = ( data ) => {
  const { loginStatus, url, clientActionType, navigationType } = data || {};

  // Based on DXL response ( clientActionType & NavigationType) Button component handles the following
  // Pop out legacy Login incase of ATG header
  // pop out Sign in Form incase of DSOTF header
  const dispatchLogin = clientActionType === CLIENT_ACTION_TYPES.LEGACY_LOGIN || navigationType === DXL_NAVIGATION_TYPE.Flyout;
  if( !loginStatus && dispatchLogin ){
    return;
  }
  global.open( url, '_self' );
};

/**
 * Property type definitions
 *
 * @typedef ProductPresaleProps
 * @type {object}
 * @property {string} perkLabel - Sets the label for the product presale
 * @property {string} perkMessage - Sets the perkMessage property
 * @property {object.<{label: string, url: string}>} perkAction - Sets the action label and url for product presale
 * @property {string} errorLabel - Sets the label for the product presale
 * @property {boolean} bookmarked - Flag to render heart icon's state
 * @property {object.<{label: string, graphql: string}>} bookmarkAction - Sets the bookmarkAction properties
 * @property {object.<{label: string, graphql: string}>} removeBookmarkAction - Sets the removeBookmarkAction properties
 */
export const propTypes = {
  perkLabel: PropTypes.string,
  perkMessage: PropTypes.string,
  perkAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  errorLabel: PropTypes.string,
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string,
    clientActionType: PropTypes.oneOfType( [PropTypes.object, PropTypes.string] ),
    navigationType: PropTypes.oneOfType( [PropTypes.object, PropTypes.string] )
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} bookmarked=false - Flag to specify if the bookmark is filled or not, default is false
 */
export const defaultProps = {
  bookmarked: false
};

ProductPresale.propTypes = propTypes;
ProductPresale.defaultProps = defaultProps;
ProductPresale.clickHandler = clickHandler;

export default ProductPresale;
